// Central registry mapping URL pathname → { label, icon, render() }.
// Used by:
//   - TabsContext (to compute open-tab metadata when the URL changes)
//   - TabContent (to render the page component once and keep it mounted)
//   - TabBar / Layout sidebar (label + icon for the tab pill)
//
// IMPORTANT: every page reachable from the sidebar must appear here.
// The path strings must match `to` values used in Layout's SECTIONS array.
import {
  LayoutDashboard, FolderKanban, Building2, Users, Package,
  Warehouse, ShoppingCart, ReceiptText, Wallet, Shield,
  FileBarChart2, HardHat, Briefcase, BookOpen,
  Banknote, ArrowDownToLine, FileText as FileTextIcon, UserSquare2,
  Scale, Calculator, FileSignature, Hash, Building, CalendarRange,
  Library, Ruler, FolderTree, ShieldCheck, Inbox, ClipboardList,
  Home as HomeIcon, BadgeDollarSign, CreditCard, Send, Landmark,
  PackageMinus, PackagePlus, ListChecks, ArrowRightLeft, Database,
  Play, SlidersHorizontal,
} from "lucide-react";

import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ClientsPage from "@/pages/ClientsPage";
import ItemsPage from "@/pages/ItemsPage";
import { Members } from "@/pages/ContractorsMembers";
import VendorsPage from "@/pages/VendorsPage";
import Inventory from "@/pages/Inventory";
import Estimates from "@/pages/Estimates";
import ApprovedEstimateQueue from "@/pages/ApprovedEstimateQueue";
import OpeningBalanceWizard from "@/pages/OpeningBalanceWizard";
import GlCleanupPage from "@/pages/GlCleanupPage";
import PiStockAuditPage from "@/pages/PiStockAuditPage";
import ProjectOpsControlPage from "@/pages/ProjectOpsControlPage";
import InvoicePage from "@/pages/InvoicePage";
import Assets from "@/pages/Assets";
import Ledgers from "@/pages/Ledgers";
import TrialBalance from "@/pages/TrialBalance";
import GeneralLedger from "@/pages/GeneralLedger";
import Documents from "@/pages/Documents";
import Reports from "@/pages/ReportsV2";
import IssuedStatementPage from "@/pages/IssuedStatementPage";
import ProjectPnlPage from "@/pages/ProjectPnlPage";
import LabourLibraryPage from "@/pages/LabourLibraryPage";
import CategoryMasterPage from "@/pages/CategoryMasterPage";
import BranchPnlPage from "@/pages/BranchPnlPage";
import VerticalPnlPage from "@/pages/VerticalPnlPage";
import WarehouseInventoryPage from "@/pages/WarehouseInventoryPage";
import CostCentreExpensePage from "@/pages/CostCentreExpensePage";
import CompanyPnlPage from "@/pages/CompanyPnlPage";
import ControlCentrePage from "@/pages/ControlCentrePage";
import GSTPage from "@/pages/GSTPage";
import UsersPage from "@/pages/Users";
import Numbering from "@/pages/Numbering";
import CompanySetupPage from "@/pages/CompanySetupPage";
import GstApiConfigPage from "@/pages/GstApiConfigPage";
import FinancialYearPage from "@/pages/FinancialYearPage";
import LibraryPage from "@/pages/LibraryPage";
import ApprovalsInboxPage from "@/pages/ApprovalsInboxPage";
import ApprovalMatrixPage from "@/pages/ApprovalMatrixPage";
import JMSPage from "@/pages/JMSPage";
import StoreRentPaymentPage from "@/pages/StoreRentPaymentPage";
import SalaryPaymentPage from "@/pages/SalaryPaymentPage";
import GeneralPaymentPage from "@/pages/GeneralPaymentPage";
import BillPaymentPage from "@/pages/BillPaymentPage";
import PostPaymentPage from "@/pages/PostPaymentPage";
import BankReconPage from "@/pages/BankReconPage";
import BankLedgerPage from "@/pages/BankLedgerPage";
import IssueVoucherPage from "@/pages/IssueVoucherPage";
import ReturnVoucherPage from "@/pages/ReturnVoucherPage";
import ReturnableRegisterPage from "@/pages/ReturnableRegisterPage";
import StockTransferPage from "@/pages/StockTransferPage";
import MaterialTransfersPage from "@/pages/MaterialTransfersPage";
import WarehouseTransfersPage from "@/pages/WarehouseTransfersPage";
import WarehouseMovementsPage from "@/pages/WarehouseMovementsPage";
import StockAdjustmentsPage from "@/pages/StockAdjustmentsPage";
import ProjectHealthDashboardPage from "@/pages/ProjectHealthDashboardPage";
import OpsDashboardPage from "@/pages/OpsDashboardPage";
import AssetLifecyclePage from "@/pages/AssetLifecyclePage";
import LowStockPage from "@/pages/LowStockPage";
import DataToolsPage from "@/pages/DataToolsPage";
// Sprint 2026-Feb-11g — Email Configuration (Admin → System Configuration)
import EmailConfigurationPage from "@/pages/admin/EmailConfigurationPage";

// Sprint 2026-Feb-12 — Estimate Pending Ageing Summary (Client POC Wise)
import EstimateAgeingReport from "@/pages/reports/EstimateAgeingReport";
import AgingOutstandingReport from "@/pages/reports/AgingOutstandingReport";
import ScanActivityReport from "@/pages/reports/ScanActivityReport";
import IdentityAuditReport from "@/pages/reports/IdentityAuditReport";
import IssuedItemsReport from "@/pages/reports/IssuedItemsReport";
import ReturnablesReport from "@/pages/reports/ReturnablesReport";
import TdsRecoveryReport from "@/pages/reports/TdsRecoveryReport";
import GLAuditPage from "@/pages/reports/GLAuditPage";
import FinancialIntegrityPage from "@/pages/reports/FinancialIntegrityPage";
import BalanceSheetPage from "@/pages/reports/BalanceSheetPage";
import ProfitLossPage from "@/pages/reports/ProfitLossPage";
import ReceivablesReports from "@/pages/reports/ReceivablesReports";
import PerformancePnl from "@/pages/reports/PerformancePnl";
import ReceivableRetentions from "@/pages/ReceivableRetentions";
import DebtorDashboard from "@/pages/DebtorDashboard";
import BulkReceipts from "@/pages/BulkReceipts";
import ReceiptVoucherRegister from "@/pages/ReceiptVoucherRegister";
import DueDateEditor from "@/pages/DueDateEditor";

// Sprint 2026-Jul — VoltLedger Invoice Follow-up Queue (invoice lifecycle
// workbench: SES, statuses, due dates, retention, CN follow-up).
import InvoiceQueue from "@/pages/InvoiceQueue";

// Sprint 2026-Jun — VoltLedger integration Phase G: HR Compliance
// (RC-based Apr–Mar monthly compliance grid over Project contracts).
import HRCompliance from "@/pages/HRCompliance";

// Sprint 2026-Feb — ONE (Official Note Entry) module. Digitises the
// paper approval-note workflow with L1..L5 sequential approvals,
// queries, and Policy-only revisions. Backend: /api/one/*
import OneNotes from "@/pages/OneNotes";

// Cost Centre Master (post MA-R1 · Feb 2026) — now under Administration.
// The legacy Management Accounting scaffolding (Categories, Eligibility,
// Pending Queue, Allocation Register) has been removed.
import MaCostCentresPage from "@/pages/ma/MaCostCentresPage";

// Overhead Allocation Engine (MA-R2 · Feb 2026) — Rules Master + Basis
// Snapshots. Allocates Internal/Corporate project costs to Revenue
// Projects. MA-R3 (Feb 2026) adds Run Workbench + Allocation Register.
import AllocationRulesPage from "@/pages/overhead/AllocationRulesPage";
import BasisSnapshotsPage from "@/pages/overhead/BasisSnapshotsPage";
import AllocationRunsPage from "@/pages/overhead/AllocationRunsPage";
import AllocationRegisterPage from "@/pages/overhead/AllocationRegisterPage";

// Journal Voucher (Sprint 2026-Feb Phase 1 — EB only). FC/OE tabs added
// in Phase 2 inside the same component.
import JournalVouchersPage from "@/pages/JournalVouchersPage";
import JVLedgersPage from "@/pages/JVLedgersPage";
// Jun-2026 — OEM Complaint module (vendor chase mails for client-site issues)
import OemComplaintsPage from "@/pages/OemComplaintsPage";

// Map: pathname → { label, icon, render }
export const TAB_REGISTRY = {
  "/": { label: "Dashboard", icon: LayoutDashboard, render: () => <Dashboard /> },
  "/project-health": { label: "Project Health", icon: FileBarChart2, render: () => <ProjectHealthDashboardPage /> },
  "/ops-dashboard": { label: "Operations Dashboard", icon: HardHat, render: () => <OpsDashboardPage /> },

  // Masters
  "/projects": { label: "Projects", icon: FolderKanban, render: () => <Projects /> },
  "/clients": { label: "Clients", icon: Users, render: () => <ClientsPage /> },
  "/vendors": { label: "Vendors", icon: HardHat, render: () => <VendorsPage /> },
  "/members": { label: "Members", icon: UserSquare2, render: () => <Members /> },
  "/items": { label: "Items", icon: Package, render: () => <ItemsPage /> },
  "/masters/bank-ledger": { label: "Bank Ledger", icon: Banknote, render: () => <BankLedgerPage /> },

  // Inventory & Assets
  "/inventory": { label: "Stock", icon: Warehouse, render: () => <Inventory /> },
  "/inventory/low-stock": { label: "Low Stock Alert", icon: Warehouse, render: () => <LowStockPage /> },
  "/assets": { label: "Assets", icon: Briefcase, render: () => <Assets /> },
  "/issue-vouchers": { label: "Issue Voucher", icon: PackageMinus, render: () => <IssueVoucherPage />, autoSpawnsForm: true },
  "/return-vouchers": { label: "Return Voucher", icon: PackagePlus, render: () => <ReturnVoucherPage />, autoSpawnsForm: true },
  "/returnable-register": { label: "Returnable Register", icon: ListChecks, render: () => <ReturnableRegisterPage /> },
  "/stock-transfers": { label: "Stock Transfer", icon: ArrowRightLeft, render: () => <StockTransferPage />, autoSpawnsForm: true },
  "/material-transfers": { label: "Material / Asset Transfer", icon: ArrowRightLeft, render: () => <MaterialTransfersPage /> },
  "/warehouse-transfers": { label: "Warehouse Transfer", icon: ArrowRightLeft, render: () => <WarehouseTransfersPage /> },
  "/warehouse-movements": { label: "Warehouse Movements", icon: ArrowRightLeft, render: () => <WarehouseMovementsPage /> },
  "/stock-adjustments": { label: "Stock Adjustment", icon: ClipboardList, render: () => <StockAdjustmentsPage /> },
  "/asset-lifecycle": { label: "Asset/Inventory Lifecycle", icon: Scale, render: () => <AssetLifecyclePage /> },

  // Transactions
  "/estimates": { label: "Estimates", icon: FileSignature, render: () => <Estimates />, autoSpawnsForm: true },
  "/oem-complaints": { label: "OEM Complaint", icon: ClipboardList, render: () => <OemComplaintsPage /> },
  "/estimates/approved-queue": { label: "Approved Estimate Queue", icon: ListChecks, render: () => <ApprovedEstimateQueue /> },
  "/jms": { label: "JMS", icon: ClipboardList, render: () => <JMSPage /> },
  "/sales-invoices": { label: "Sales Invoices", icon: ReceiptText, render: () => <InvoicePage type="sales" />, autoSpawnsForm: true },
  "/sales-credit-notes": { label: "Sales Credit Note", icon: ReceiptText, render: () => <InvoicePage type="sales_credit_note" />, autoSpawnsForm: true },
  "/sales-debit-notes": { label: "Sales Debit Note", icon: ReceiptText, render: () => <InvoicePage type="sales_debit_note" />, autoSpawnsForm: true },
  // Jul-2026 — Receivables consolidation: the legacy standalone Payment
  // Receipt page (/payment-receipts, /payments) was retired. Customer
  // collections flow only through Receivables → Bulk Receipts.

  // Purchase
  "/purchase-orders": { label: "Purchase Order", icon: ClipboardList, render: () => <InvoicePage type="purchase_order" />, autoSpawnsForm: true },
  "/purchase-invoices": { label: "Purchase Invoice", icon: ShoppingCart, render: () => <InvoicePage type="purchase" />, autoSpawnsForm: true },
  "/purchase/store-rent": { label: "Store Rent", icon: HomeIcon, render: () => <StoreRentPaymentPage /> },
  "/purchase/salary": { label: "Salary Payment", icon: BadgeDollarSign, render: () => <SalaryPaymentPage /> },
  "/purchase/general": { label: "General Payment", icon: CreditCard, render: () => <GeneralPaymentPage /> },
  "/purchase/bill-payment": { label: "Bill Payment", icon: Wallet, render: () => <BillPaymentPage /> },
  "/purchase/post-payment": { label: "Post Payment", icon: Send, render: () => <PostPaymentPage /> },
  "/bank-reconciliation": { label: "Bank Recon", icon: Landmark, render: () => <BankReconPage /> },

  // Approvals
  "/approvals": { label: "Approvals", icon: Inbox, render: () => <ApprovalsInboxPage /> },

  // Official Note Entry (ONE) — Sprint 2026-Feb
  "/one": { label: "Official Notes", icon: ClipboardList, render: () => <OneNotes /> },

  // Cost Centre Master (post MA-R1 · under Administration)
  "/management-accounting/cost-centres": { label: "Cost Centres",       icon: Building,      render: () => <MaCostCentresPage /> },

  // Overhead Allocation Engine (MA-R2 · Feb 2026)
  "/overhead-allocation/rules":     { label: "Allocation Rules",   icon: ListChecks,    render: () => <AllocationRulesPage /> },
  "/overhead-allocation/snapshots": { label: "Basis Snapshots",    icon: ClipboardList, render: () => <BasisSnapshotsPage /> },
  // Overhead Allocation Run Workbench + Register (MA-R3 · Feb 2026)
  "/overhead-allocation/runs":      { label: "Run Workbench",      icon: Play,          render: () => <AllocationRunsPage /> },
  "/overhead-allocation/register":  { label: "Allocation Register",icon: ClipboardList, render: () => <AllocationRegisterPage /> },

  // Accounting
  "/ledgers": { label: "Ledgers", icon: BookOpen, render: () => <Ledgers /> },
  "/trial-balance": { label: "Trial Balance", icon: Scale, render: () => <TrialBalance /> },
  "/reports/gl-audit": { label: "GL Integrity Audit", icon: Scale, render: () => <GLAuditPage /> },
  "/reports/financial-integrity": { label: "Financial Integrity", icon: ShieldCheck, render: () => <FinancialIntegrityPage /> },
  "/reports/balance-sheet": { label: "Balance Sheet (Sch III)", icon: Landmark, render: () => <BalanceSheetPage /> },
  "/opening-balance": { label: "Opening Balance Wizard", icon: Scale, render: () => <OpeningBalanceWizard /> },
  "/reports/profit-loss": { label: "Profit & Loss (Sch III)", icon: FileBarChart2, render: () => <ProfitLossPage /> },
  "/general-ledger": { label: "General Ledger", icon: Calculator, render: () => <GeneralLedger /> },
  // Journal Vouchers (Sprint 2026-Feb) — Single entry point. The page
  // exposes a Category selector (EB / FC / OE / OI) internally. Only
  // EB is active in Phase 1; FC/OE land in Phase 2.
  "/journal-vouchers": { label: "Journal Voucher", icon: Calculator, render: () => <JournalVouchersPage />, autoSpawnsForm: false },
  // Sprint 2026-Feb JV Stabilization — canonical chart-of-accounts
  // for JV lines.
  "/jv-ledgers": { label: "JV Ledger Master", icon: Calculator, render: () => <JVLedgersPage />, autoSpawnsForm: false },

  // Documents & Reports
  "/documents": { label: "Documents", icon: FileTextIcon, render: () => <Documents /> },
  "/reports": { label: "Reports", icon: FileBarChart2, render: () => <Reports /> },
  "/reports/project-pnl": { label: "Project P&L", icon: FileBarChart2, render: () => <ProjectPnlPage /> },
  "/reports/branch-pnl": { label: "Branch P&L", icon: FileBarChart2, render: () => <BranchPnlPage /> },
  "/reports/vertical-pnl": { label: "Vertical P&L", icon: FileBarChart2, render: () => <VerticalPnlPage /> },
  "/reports/warehouse-inventory": { label: "Warehouse Inventory", icon: FileBarChart2, render: () => <WarehouseInventoryPage /> },
  "/reports/cost-centre-expense": { label: "Cost Centre Expense", icon: FileBarChart2, render: () => <CostCentreExpensePage /> },
  "/reports/company-pnl": { label: "Company P&L (Schedule III)", icon: FileBarChart2, render: () => <CompanyPnlPage /> },
  "/reports/issued-statement": { label: "Issued Statement", icon: FileBarChart2, render: () => <IssuedStatementPage /> },
  "/reports/estimate-ageing": { label: "Estimate Ageing Summary", icon: FileBarChart2, render: () => <EstimateAgeingReport /> },
  "/reports/aging-outstanding": { label: "Aging Outstanding (Receivables)", icon: FileBarChart2, render: () => <AgingOutstandingReport /> },
  "/reports/scan-activity": { label: "Scan Activity Report", icon: FileBarChart2, render: () => <ScanActivityReport /> },
  "/reports/identity-audit": { label: "Identity Audit Sweep", icon: FileBarChart2, render: () => <IdentityAuditReport /> },
  "/reports/issued-items": { label: "Issued Items Report", icon: FileBarChart2, render: () => <IssuedItemsReport /> },
  "/reports/returnables": { label: "Returnable Tracking", icon: FileBarChart2, render: () => <ReturnablesReport /> },
  "/reports/receivables": { label: "Receivables Reports", icon: FileBarChart2, render: () => <ReceivablesReports /> },
  "/reports/performance-pnl": { label: "Project Monthly Performance P&L", icon: FileBarChart2, render: () => <PerformancePnl /> },
  "/receivable-retentions": { label: "Retentions (Receivable)", icon: Landmark, render: () => <ReceivableRetentions /> },
  "/invoice-queue": { label: "Invoice Queue", icon: ListChecks, render: () => <InvoiceQueue /> },
  "/debtor-dashboard": { label: "Debtor/HR Management Dashboard", icon: LayoutDashboard, render: () => <DebtorDashboard /> },
  "/bulk-receipts": { label: "Bulk Receipts", icon: ArrowDownToLine, render: () => <BulkReceipts /> },
  "/receipt-voucher-register": { label: "Receipt Voucher Register", icon: ClipboardList, render: () => <ReceiptVoucherRegister /> },
  "/reports/tds-recovery": { label: "TDS Recovery", icon: ClipboardList, render: () => <TdsRecoveryReport /> },
  "/receivables/due-dates": { label: "Due Date Editor", icon: CalendarRange, render: () => <DueDateEditor /> },
  "/gst": { label: "GST Centre", icon: FileBarChart2, render: () => <GSTPage /> },

  // Compliance
  "/hr-compliance": { label: "HR Compliance", icon: ShieldCheck, render: () => <HRCompliance /> },
  "/labour-library": { label: "Labour Library", icon: HardHat, render: () => <LabourLibraryPage /> },
  "/category-master": { label: "Category Master", icon: FolderTree, render: () => <CategoryMasterPage /> },

  // Administration
  "/settings/company": { label: "Company Setup", icon: Building, render: () => <CompanySetupPage /> },
  "/settings/gst-api-config": { label: "GST E-Invoice API", icon: ShieldCheck, render: () => <GstApiConfigPage /> },
  "/settings/financial-year": { label: "Financial Year", icon: CalendarRange, render: () => <FinancialYearPage /> },
  "/library/item-groups": { label: "Item Groups", icon: FolderTree, render: () => <LibraryPage kind="item-groups" /> },
  "/library/project-groups": { label: "Project Groups", icon: FolderKanban, render: () => <LibraryPage kind="project-groups" /> },
  "/library/hsn-sac": { label: "HSN / SAC", icon: Hash, render: () => <LibraryPage kind="hsn-sac" /> },
  "/library/uom": { label: "UOM", icon: Ruler, render: () => <LibraryPage kind="uom" /> },
  "/users": { label: "Users & Roles", icon: Shield, render: () => <UsersPage /> },
  "/settings/approval-matrix": { label: "Approval Matrix", icon: ShieldCheck, render: () => <ApprovalMatrixPage /> },
  "/numbering": { label: "Numbering Series", icon: Library, render: () => <Numbering /> },
  "/settings/email": { label: "Email Configuration", icon: Send, render: () => <EmailConfigurationPage /> },
  "/settings/data-tools": { label: "Data Tools", icon: Database, render: () => <DataToolsPage /> },
  "/settings/project-ops-control": { label: "Project Ops Control", icon: SlidersHorizontal, render: () => <ProjectOpsControlPage /> },
  "/settings/gl-cleanup": { label: "GL Cleanup (Orphans)", icon: Database, render: () => <GlCleanupPage /> },
  "/settings/pi-stock-audit": { label: "PI Stock Audit", icon: Package, render: () => <PiStockAuditPage /> },
  "/control-centre": { label: "Financial Control Centre", icon: ShieldCheck, render: () => <ControlCentrePage /> },
};

export const MAX_TABS = 12;
export const DEFAULT_TAB_PATH = "/";
